<script setup>
import { computed, ref, onMounted, onUnmounted, toRef } from 'vue';
import { useFunctionGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { fromUnixTime, format } from 'date-fns';

const props = defineProps({
  conversation: {
    type: Object,
    default: () => ({}),
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const { currentAccount } = useAccount();

const inboxIdRef = toRef(props, 'inboxId');
const inbox = useFunctionGetter('inboxes/getInbox', inboxIdRef);

const elapsedAtendimento = ref(0);
const elapsedFila = ref(0);
const copied = ref(false);
let timerInterval = null;

// Timers freeze when conversation is resolved or snoozed (not actively being handled)
const isLive = computed(() => {
  const status = props.conversation?.status;
  return status === 'open' || status === 'pending';
});

// Label switches: no assignee → "Nesta fila há"; with assignee → "Neste time há"
const queueOrTeamLabelKey = computed(() =>
  props.conversation?.meta?.assignee?.id || props.conversation?.assignee_id
    ? 'CONVERSATION_SIDEBAR.ACCORDION.ISP_SESSION_IN_TEAM'
    : 'CONVERSATION_SIDEBAR.ACCORDION.ISP_SESSION_IN_QUEUE'
);

const createdAtFormatted = computed(() => {
  const ts = props.conversation?.created_at;
  if (!ts) return '—';
  const date = fromUnixTime(ts);
  return format(date, 'dd/MM/yyyy HH:mm:ss');
});

const protocolo = computed(() => {
  // GK-HUB: backend is the single source of truth (prefix + year + display_id, year in the account
  // timezone). Prefer the serialized value so the sidebar matches the note and the webhook exactly;
  // fall back to a client-side build only if the payload predates this change.
  const fromPayload = props.conversation?.protocolo;
  if (fromPayload) return fromPayload;
  // GK-HUB: mirror Account::DEFAULT_PROTOCOLO_PREFIX so the client-side fallback matches the backend.
  const prefix =
    currentAccount.value?.custom_attributes?.protocolo_prefix || 'GKHUB';
  // In Chatwoot API, display_id is serialized as `id`
  const displayId = props.conversation?.id;
  if (!displayId) return null;
  const createdAt = props.conversation?.created_at;
  const year = createdAt ? new Date(createdAt * 1000).getFullYear() : '';
  return `${prefix}${year}${displayId}`;
});

const channelLabel = computed(() => {
  const type = inbox.value?.channel_type;
  const labels = {
    'Channel::Whatsapp': 'WhatsApp',
    'Channel::WebWidget': 'Web Chat',
    'Channel::Email': 'E-mail',
    'Channel::Api': 'API',
    'Channel::FacebookPage': 'Facebook',
    'Channel::TelegramBot': 'Telegram',
    'Channel::Sms': 'SMS',
    'Channel::Line': 'LINE',
    'Channel::TwitterProfile': 'Twitter',
  };
  return labels[type] || inbox.value?.name || '—';
});

const channelIdentifier = computed(() => {
  const phone = inbox.value?.phone_number;
  if (phone) return phone;
  return '';
});

const isWhatsApp = computed(() => {
  const type = inbox.value?.channel_type;
  return type === 'Channel::Whatsapp';
});

function formatElapsed(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}min`;
  if (m > 0) return `${m} min ${s.toString().padStart(2, '0')}s`;
  return `${s}s`;
}

function updateTimers() {
  const createdAt = props.conversation?.created_at;
  if (!createdAt) return;
  // Freeze at last_activity_at when conversation is resolved/snoozed
  const referenceNow = isLive.value
    ? Math.floor(Date.now() / 1000)
    : props.conversation?.last_activity_at || Math.floor(Date.now() / 1000);
  elapsedAtendimento.value = Math.max(0, referenceNow - createdAt);
  // Priority: team_assigned_at (when team changed) > waiting_since > created_at
  const teamAssignedAt =
    props.conversation?.custom_attributes?.team_assigned_at;
  const waitingSince = props.conversation?.waiting_since;
  const filaRef = teamAssignedAt || waitingSince || createdAt;
  elapsedFila.value = Math.max(0, referenceNow - filaRef);
}

async function copyProtocolo() {
  if (!protocolo.value || copied.value) return;
  try {
    await navigator.clipboard.writeText(protocolo.value);
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  } catch {
    // Fallback for non-HTTPS
    const textarea = document.createElement('textarea');
    textarea.value = protocolo.value;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  }
}

onMounted(() => {
  updateTimers();
  timerInterval = setInterval(updateTimers, 1000);
});

onUnmounted(() => {
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
});
</script>

<template>
  <div class="isp-session-info">
    <!-- Aberto em -->
    <div class="isp-session-row">
      <span class="isp-session-icon">
        <svg
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="1.8"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
      </span>
      <span class="isp-session-label">
        {{ $t('CONVERSATION_SIDEBAR.ACCORDION.ISP_SESSION_OPENED') }}
      </span>
      <span class="isp-session-value">{{ createdAtFormatted }}</span>
    </div>

    <!-- Em atendimento -->
    <div class="isp-session-row">
      <span class="isp-session-icon">
        <svg
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="1.8"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
      </span>
      <span class="isp-session-label">
        {{ $t('CONVERSATION_SIDEBAR.ACCORDION.ISP_SESSION_IN_SERVICE') }}
      </span>
      <span class="isp-session-value" :class="{ 'isp-session-live': isLive }">
        {{ formatElapsed(elapsedAtendimento) }}
      </span>
    </div>

    <!-- Na fila há -->
    <div class="isp-session-row">
      <span class="isp-session-icon">
        <svg
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="1.8"
        >
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
        </svg>
      </span>
      <span class="isp-session-label">
        {{ $t(queueOrTeamLabelKey) }}
      </span>
      <span class="isp-session-value" :class="{ 'isp-session-live': isLive }">
        {{ formatElapsed(elapsedFila) }}
      </span>
    </div>

    <!-- Canal -->
    <div class="isp-session-row">
      <span class="isp-session-icon">
        <svg
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="1.8"
        >
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
        </svg>
      </span>
      <span class="isp-session-label">
        {{ $t('CONVERSATION_SIDEBAR.ACCORDION.ISP_SESSION_CHANNEL') }}
      </span>
      <span class="isp-session-channel-badge">
        <svg
          v-if="isWhatsApp"
          class="isp-session-whatsapp-icon"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path
            d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347"
          />
        </svg>
        {{ channelLabel }}
        <template v-if="channelIdentifier">
          · {{ channelIdentifier }}
        </template>
      </span>
    </div>

    <!-- Protocolo -->
    <div v-if="protocolo" class="isp-session-row isp-session-protocolo-row">
      <div class="isp-session-protocolo-left">
        <span class="isp-session-icon">
          <svg
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"
            />
            <rect x="9" y="3" width="6" height="4" rx="1" />
          </svg>
        </span>
        <span class="isp-session-label">
          {{ $t('CONVERSATION_SIDEBAR.ACCORDION.ISP_SESSION_PROTOCOL') }}
        </span>
        <span class="isp-session-mono">{{ protocolo }}</span>
      </div>
      <button
        class="isp-session-copy-btn"
        :title="$t('CONVERSATION_SIDEBAR.ACCORDION.ISP_SESSION_COPY')"
        @click="copyProtocolo"
      >
        <!-- Copy icon -->
        <svg
          v-if="!copied"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
        </svg>
        <!-- Check icon -->
        <svg
          v-else
          class="isp-session-check-icon"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2.5"
        >
          <path d="M5 13l4 4L19 7" />
        </svg>
      </button>
    </div>
  </div>
</template>

<style scoped>
.isp-session-info {
  @apply flex flex-col border-b border-n-weak px-4 pb-3;
  padding-top: 10px;
  margin-bottom: 4px;
  gap: 6px;
  background: var(--n-alpha-2);
}

.isp-session-row {
  @apply flex items-center gap-2 text-xs;
  height: 20px;
}

.isp-session-icon {
  @apply flex shrink-0 items-center justify-center;
  width: 15px;
  height: 15px;
}

.isp-session-icon svg {
  @apply text-n-slate-10;
  width: 15px;
  height: 15px;
}

.isp-session-label {
  @apply shrink-0 text-n-slate-10;
  font-size: 11px;
}

.isp-session-value {
  @apply font-medium text-n-slate-12;
  font-size: 12px;
}

.isp-session-live {
  @apply text-n-teal-11;
}

.isp-session-live::after {
  content: '';
  @apply ml-1.5 inline-block rounded-full;
  width: 5px;
  height: 5px;
  background: var(--n-teal-9);
  vertical-align: middle;
  animation: isp-pulse-dot 2s ease-in-out infinite;
}

@keyframes isp-pulse-dot {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.isp-session-mono {
  @apply rounded border border-n-weak text-n-slate-11;
  font-family: 'SF Mono', 'Fira Code', ui-monospace, monospace;
  font-size: 11px;
  padding: 0 6px;
  line-height: 18px;
  background: var(--n-alpha-3);
}

.isp-session-channel-badge {
  @apply inline-flex items-center gap-1 rounded-full border border-n-weak text-n-slate-11;
  font-size: 11px;
  font-weight: 500;
  padding: 0 7px;
  line-height: 18px;
  background: var(--n-alpha-3);
}

.isp-session-whatsapp-icon {
  width: 12px;
  height: 12px;
  color: #25d366;
}

.isp-session-protocolo-row {
  @apply justify-between;
}

.isp-session-protocolo-left {
  @apply flex items-center gap-2;
}

.isp-session-copy-btn {
  @apply flex shrink-0 cursor-pointer items-center justify-center rounded-md border border-n-weak;
  width: 30px;
  height: 30px;
  background: var(--n-alpha-2);
  transition: all 0.15s ease;
}

.isp-session-copy-btn svg {
  @apply text-n-slate-10;
  width: 14px;
  height: 14px;
}

.isp-session-copy-btn:hover {
  background: var(--n-blue-3);
  border-color: var(--n-blue-6);
}

.isp-session-copy-btn:hover svg {
  @apply text-n-blue-11;
}

.isp-session-copy-btn:active {
  transform: scale(0.92);
}

.isp-session-check-icon {
  @apply text-n-teal-9;
}
</style>
